import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Gift,
  Minus,
  PackageOpen,
  Plus,
  RefreshCw,
  ShoppingCart,
  Trash2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { qrPrintApi } from '../../api/qrPrintApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { getMediaUrl } from '../../utils/qrPrintHelpers';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';

import '../../styles/QrPrint/QrPrintCart.css';

const translateFallback = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const OPTION_LABEL_KEYS = {
  size: 'qrPrint.common.size',
  selected_size: 'qrPrint.common.size',
  material: 'qrPrint.common.material',
  selected_material: 'qrPrint.common.material',
  color: 'qrPrint.common.color',
  selected_color: 'qrPrint.common.color',
  surface_finish: 'qrPrint.designer.surfaceFinish',
  base_style: 'qrPrint.designer.baseStyle',
  thickness: 'qrPrint.designer.thickness',
  stand_angle: 'qrPrint.designer.standAngle',
  design_variant: 'qrPrint.designer.designVariant',
  qr_frame: 'qrPrint.designer.qrFrame',
  accent_mode: 'qrPrint.designer.accentMode',
};

const OPTION_VALUE_KEYS = {
  surface_finish: {
    glossy: 'qrPrint.designer.finishGlossy',
    matte: 'qrPrint.designer.finishMatte',
    frosted: 'qrPrint.designer.finishFrosted',
  },
  base_style: {
    classic: 'qrPrint.designer.baseClassic',
    wide: 'qrPrint.designer.baseWide',
    minimal: 'qrPrint.designer.baseMinimal',
  },
  design_variant: {
    editorial: 'qrPrint.designer.variantEditorial',
    minimal: 'qrPrint.designer.variantMinimal',
    luxury: 'qrPrint.designer.variantLuxury',
  },
  qr_frame: {
    none: 'qrPrint.designer.frameNone',
    outlined: 'qrPrint.designer.frameOutlined',
    filled: 'qrPrint.designer.frameFilled',
  },
  accent_mode: {
    rail: 'qrPrint.designer.accentRail',
    corner: 'qrPrint.designer.accentCorner',
    corners: 'qrPrint.designer.accentCorner',
    none: 'qrPrint.designer.accentNone',
  },
};

// Normalize option names received from the backend before translating them.
const normalizeOptionKey = (value) =>
  String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();

const humanizeOptionValue = (value) =>
  String(value ?? '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase(),
    );

const getOptionLabel = (t, key) => {
  const normalizedKey =
    normalizeOptionKey(key);

  const translationKey =
    OPTION_LABEL_KEYS[normalizedKey];

  if (translationKey) {
    return translateFallback(
      t,
      translationKey,
      humanizeOptionValue(key),
    );
  }

  return humanizeOptionValue(key);
};

const getOptionValue = (
  t,
  key,
  value,
) => {
  const normalizedKey =
    normalizeOptionKey(key);

  const normalizedValue =
    normalizeOptionKey(value);

  const translationKey =
    OPTION_VALUE_KEYS[
      normalizedKey
    ]?.[normalizedValue];

  if (translationKey) {
    return translateFallback(
      t,
      translationKey,
      humanizeOptionValue(value),
    );
  }

  return humanizeOptionValue(value);
};

const formatNumber = (
  value,
  language,
) => {
  const number = Number(value || 0);

  try {
    return new Intl.NumberFormat(
      language,
    ).format(number);
  } catch {
    return String(number);
  }
};

const formatCartMoney = (
  value,
  currency = 'MAD',
  language,
) => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '—';
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '—';
  }

  try {
    return new Intl.NumberFormat(
      language,
      {
        style: 'currency',
        currency:
          currency || 'MAD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    ).format(number);
  } catch {
    return `${number.toFixed(2)} ${
      currency || 'MAD'
    }`;
  }
};

const minimumQuantity = (item) =>
  Math.max(Number(item?.product?.minimum_quantity || 1), 1);

export default function QrPrintCart() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const direction = String(language || '')
    .toLowerCase()
    .startsWith('ar')
    ? 'rtl'
    : 'ltr';

  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    estimated_total: '0.00',
    items_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [busyItemId, setBusyItemId] = useState(null);
  const [removingItemId, setRemovingItemId] = useState(null);
  const [error, setError] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [initialOffer, setInitialOffer] = useState('');
  const [offerMessage, setOfferMessage] = useState('');

  const itemsCount = useMemo(
    () =>
      Number(
        meta?.items_count ||
          items.reduce(
            (total, item) => total + Number(item.quantity || 0),
            0,
          ),
      ),
    [items, meta?.items_count],
  );

  // Promotion is calculated by Laravel from database prices, never from the browser.
  const promotion = meta?.promotion || null;
  const freeQuantity = Number(promotion?.free_quantity || 0);
  const eligibleQuantity = Number(promotion?.eligible_quantity || 0);
  const promotionDiscount = Number(promotion?.discount || 0);
  const estimatedSubtotal = meta?.estimated_subtotal ?? null;
  const remainingForReward = eligibleQuantity % 10 === 0 ? 0 : 10 - (eligibleQuantity % 10);
  const promotionLinesById = useMemo(
    () => new Map((promotion?.lines || []).map((line) => [String(line.cart_item_id), line])),
    [promotion],
  );

  const load = async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError('');

    try {
      const response = await qrPrintApi.getCart();
      setItems(response.data?.data || []);
      setMeta(response.data?.meta || {});
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          translateFallback(
            t,
            'qrPrint.cart.loadError',
            'The cart could not be loaded.',
          ),
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocalQuantity = (itemId, quantity) => {
    setItems((current) =>
      current.map((item) =>
        item.id === itemId
          ? {
              ...item,
              quantity,
            }
          : item,
      ),
    );
  };

  const updateQuantity = async (item, value) => {
    const min = minimumQuantity(item);
    const nextQuantity = Math.max(min, Number(value || min));

    setBusyItemId(item.id);
    setError('');
    setLocalQuantity(item.id, nextQuantity);

    try {
      await qrPrintApi.updateCartItem(item.id, {
        quantity: nextQuantity,
      });
      await load(true);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          translateFallback(
            t,
            'qrPrint.cart.updateError',
            'The quantity could not be updated.',
          ),
      );
      await load(true);
    } finally {
      setBusyItemId(null);
    }
  };

  const adjustQuantity = (item, direction) => {
    updateQuantity(item, Number(item.quantity || 0) + direction);
  };

  const remove = async (itemId) => {
    setRemovingItemId(itemId);
    setError('');

    try {
      await qrPrintApi.removeCartItem(itemId);
      await load(true);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          translateFallback(
            t,
            'qrPrint.cart.removeError',
            'The item could not be removed.',
          ),
      );
    } finally {
      setRemovingItemId(null);
    }
  };

  const submitRequest = async () => {
    if (items.length === 0) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await qrPrintApi.createOrder({
        customer_note: customerNote || null,
        initial_offer: initialOffer === '' ? null : Number(initialOffer),
        offer_message: offerMessage || null,
      });

      navigate(`/qr-print/orders/${response.data?.data?.id}`);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          translateFallback(
            t,
            'qrPrint.cart.submitError',
            'The quotation request could not be submitted.',
          ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="qr-print-cart-loading" dir={direction}>
        <span className="qr-print-spinner" />
        <strong>
          {translateFallback(t, 'qrPrint.cart.loading', 'Loading print cart...')}
        </strong>
      </div>
    );
  }

  return (
    <main className="qr-print-page qr-print-cart-page" dir={direction}>
      <header className="qr-print-cart-hero">
        <div className="qr-print-cart-hero__content">
          <Link
            to="/qr-print/products"
            className="qr-print-cart-back"
            aria-label={translateFallback(
              t,
              'qrPrint.cart.backToProducts',
              'Back to print products',
            )}
          >
            <ArrowLeft size={17} />
          </Link>

          <span className="qr-print-cart-eyebrow">
            <ShoppingCart size={16} />
            {translateFallback(t, 'qrPrint.cart.eyebrow', 'Print order')}
          </span>

          <h1>{translateFallback(t, 'qrPrint.cart.title', 'Print cart')}</h1>

          <p>
            {translateFallback(
              t,
              'qrPrint.cart.description',
              'Review your customized products before requesting your quotation.',
            )}
          </p>
        </div>

        <div className="qr-print-cart-hero__summary">
          <span>{translateFallback(t, 'qrPrint.cart.items', 'Items')}</span>
          <strong>{formatNumber(itemsCount, language)}</strong>
        </div>
      </header>

      {error ? <div className="qr-print-cart-error">{error}</div> : null}

      {items.length === 0 ? (
        <div className="qr-print-empty">
          <PackageOpen size={36} />

          <div>
            <h2>
              {translateFallback(
                t,
                'qrPrint.cart.emptyTitle',
                'Your print cart is empty',
              )}
            </h2>
            <p>
              {translateFallback(
                t,
                'qrPrint.cart.emptyDescription',
                'Choose a QR print product and customize it before requesting a quote.',
              )}
            </p>
          </div>

          <Link
            to="/qr-print/products"
            className="qr-print-button qr-print-button-primary"
          >
            {translateFallback(
              t,
              'qrPrint.cart.browseProducts',
              'Browse print products',
            )}
            <ArrowRight size={17} />
          </Link>
        </div>
      ) : (
        <div className="qr-print-cart-layout">
          <section className="qr-print-cart-list">
            <div className="qr-print-cart-list__top">
              <div>
                <span>
                  {translateFallback(t, 'qrPrint.cart.cartItems', 'Cart items')}
                </span>
                <h2>
                  {translateFallback(
                    t,
                    'qrPrint.cart.reviewProducts',
                    'Review products',
                  )}
                </h2>
              </div>

              <button
                type="button"
                className="qr-print-cart-refresh"
                disabled={refreshing}
                onClick={() => load(true)}
              >
                <RefreshCw size={15} className={refreshing ? 'is-spinning' : ''} />
                {translateFallback(t, 'common.refresh', 'Refresh')}
              </button>
            </div>

            {items.map((item) => {
              const imageUrl = getMediaUrl(item.product?.main_image_path);
              const min = minimumQuantity(item);
              const isBusy = busyItemId === item.id;
              const isRemoving = removingItemId === item.id;

              return (
                <article className="qr-print-cart-item" key={item.id}>
                  <div className="qr-print-cart-item-media">
                    {imageUrl ? (
                      <img
                          src={imageUrl}
                          alt={
                            getTranslatedField(
                              item.product,
                              'name',
                              language,
                              'fr',
                            ) ||
                            item.product?.name ||
                            ''
                          }
                        />
                    ) : (
                      <PackageOpen size={30} />
                    )}
                  </div>

                  <div className="qr-print-cart-item-content">
                    <span className="qr-print-cart-item-kicker">
                      {translateFallback(
                        t,
                        'qrPrint.cart.printProduct',
                        'Print product',
                      )}
                    </span>

                    <h2>
                      {getTranslatedField(
                        item.product,
                        'name',
                        language,
                        'fr',
                      ) ||
                        item.product?.name ||
                        translateFallback(
                          t,
                          'qrPrint.common.productFallback',
                          'QR product',
                        )}
                    </h2>

                    <p>
                      {getTranslatedField(
                        item.design?.menu,
                        'name',
                        language,
                        'fr',
                      ) ||
                        item.design?.menu?.name ||
                        translateFallback(
                          t,
                          'qrPrint.cart.menuDesign',
                          'Menu design',
                        )}
                    </p>

                    <div className="qr-print-cart-options">
                      {Object.entries(item.selected_options || {})
                        .filter(([, value]) => value)
                        .map(([key, value]) => (
                          <span key={key}>
                            {getOptionLabel(
                              t,
                              key,
                            )}
                            <strong>
                              {getOptionValue(
                                t,
                                key,
                                value,
                              )}
                            </strong>
                          </span>
                        ))}
                    </div>
                  </div>

                  <div className="qr-print-cart-item-side">
                    <div className="qr-print-cart-quantity">
                      <span>
                        {translateFallback(t, 'qrPrint.designer.quantity', 'Quantity')}
                      </span>

                      <div className="qr-print-cart-stepper">
                        <button
                          type="button"
                          disabled={isBusy || Number(item.quantity) <= min}
                          onClick={() => adjustQuantity(item, -1)}
                        >
                          <Minus size={14} />
                        </button>

                        <input
                          type="number"
                          dir="ltr"
                          inputMode="numeric"
                          min={min}
                          value={item.quantity}
                          onChange={(event) =>
                            setLocalQuantity(item.id, event.target.value)
                          }
                          onBlur={(event) =>
                            updateQuantity(item, event.target.value)
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.currentTarget.blur();
                            }
                          }}
                        />

                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => adjustQuantity(item, 1)}
                        >
                          <Plus size={14} />
                        </button>
                      </div>

                      <small>
                        {translateFallback(t, 'qrPrint.cart.minimum', 'Minimum')}{' '}
                        {formatNumber(
                          min,
                          language,
                        )}
                      </small>
                    </div>

                    {Number(promotionLinesById.get(String(item.id))?.free_quantity || 0) > 0 && (
                      <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#92400e', fontWeight: 700, fontSize: 13 }}>
                        <Gift size={16} />
                        {formatNumber(promotionLinesById.get(String(item.id)).free_quantity, language)} free
                        <span style={{ fontWeight: 400 }}>
                          (−{formatCartMoney(promotionLinesById.get(String(item.id)).discount, meta?.currency || 'MAD', language)})
                        </span>
                      </div>
                    )}

                    <div className="qr-print-cart-price">
                      <span>
                        {translateFallback(t, 'qrPrint.cart.itemTotal', 'Item total')}
                      </span>
                      <strong>
                        {formatCartMoney(
                          item.estimated_total,
                          item.currency ||
                            item.product?.currency ||
                            meta?.currency ||
                            'MAD',
                          language,
                        )}
                      </strong>
                    </div>

                    <button
                      type="button"
                      className="qr-print-icon-button is-danger"
                      disabled={isRemoving}
                      onClick={() => remove(item.id)}
                      aria-label={translateFallback(t, 'common.delete', 'Delete')}
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                </article>
              );
            })}
          </section>

          <aside className="qr-print-checkout-card">
            <div className="qr-print-checkout-heading">
              <span>{translateFallback(t, 'qrPrint.cart.checkout', 'Checkout')}</span>
              <h2>
                {translateFallback(
                  t,
                  'qrPrint.cart.requestTitle',
                  'Request quotation',
                )}
              </h2>
            </div>

            {promotion && (
              <div className="qr-print-cart-promotion" style={{ padding: '14px 0', borderBottom: '1px solid #fde9c8', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#92400e', fontWeight: 700 }}>
                  <Gift size={19} />
                  <span>Buy 9, Get 1 Free</span>
                </div>
                <p style={{ margin: '8px 0 4px', fontSize: 13 }}>
                  {freeQuantity > 0
                    ? `${formatNumber(freeQuantity, language)} free ${freeQuantity === 1 ? 'product' : 'products'} applied — the cheapest eligible units are free.`
                    : remainingForReward > 0
                      ? `Add ${formatNumber(remainingForReward, language)} more eligible ${remainingForReward === 1 ? 'product' : 'products'} to receive 1 free.`
                      : 'Your promotion will be calculated from eligible items.'}
                </p>
                <small>{formatNumber(eligibleQuantity, language)} eligible units in this cart · Applies across mixed products</small>
              </div>
            )}

            {promotion && estimatedSubtotal !== null && (
              <div className="qr-print-cart-total" style={{ marginBottom: 8 }}>
                <span>Subtotal before promotion</span>
                <strong>{formatCartMoney(estimatedSubtotal, meta?.currency || 'MAD', language)}</strong>
              </div>
            )}

            {promotion && freeQuantity > 0 && (
              <div className="qr-print-cart-total" style={{ marginBottom: 8, color: '#92400e' }}>
                <span>Buy 9, Get 1 Free savings</span>
                <strong>−{formatCartMoney(promotionDiscount, meta?.currency || 'MAD', language)}</strong>
              </div>
            )}

            <div className="qr-print-cart-total">
              <span>
                {translateFallback(
                  t,
                  'qrPrint.cart.estimatedTotal',
                  'Estimated total',
                )}
              </span>
              <strong>
                {formatCartMoney(
                  meta.estimated_total,
                  meta?.currency || 'MAD',
                  language,
                )}
              </strong>
            </div>

            <label>
              <span>
                {translateFallback(
                  t,
                  'qrPrint.cart.initialOffer',
                  'Your initial offer (optional)',
                )}
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={initialOffer}
                placeholder="0.00"
                onChange={(event) => setInitialOffer(event.target.value)}
              />
            </label>

            <label>
              <span>
                {translateFallback(t, 'qrPrint.cart.offerMessage', 'Offer message')}
              </span>
              <textarea
                rows="3"
                value={offerMessage}
                placeholder={translateFallback(
                  t,
                  'qrPrint.cart.offerMessagePlaceholder',
                  'Add details about your budget or preferred delivery date...',
                )}
                onChange={(event) => setOfferMessage(event.target.value)}
              />
            </label>

            <label>
              <span>
                {translateFallback(t, 'qrPrint.cart.orderNote', 'Order note')}
              </span>
              <textarea
                rows="3"
                value={customerNote}
                placeholder={translateFallback(
                  t,
                  'qrPrint.cart.orderNotePlaceholder',
                  'Any extra instructions for the print team...',
                )}
                onChange={(event) => setCustomerNote(event.target.value)}
              />
            </label>

            <button
              type="button"
              disabled={submitting || items.length === 0}
              className="qr-print-button qr-print-button-primary qr-print-button-full"
              onClick={submitRequest}
            >
              {submitting
                ? translateFallback(t, 'qrPrint.cart.submitting', 'Submitting...')
                : translateFallback(t, 'qrPrint.cart.submit', 'Request quote')}
              <ArrowRight size={18} />
            </button>
          </aside>
        </div>
      )}
    </main>
  );
}